import { useAnimateBar } from '@mui/x-charts/hooks/animation';
import type { BarProps } from '@mui/x-charts/BarChart';
import type { ReactElement, Ref } from 'react';

type StackedBarChartRoundedTopBarProps = BarProps & {
  topSeriesKeyByRow: Array<string | null>;
  barRadius: number;
  segmentSeparatorColor: string;
  segmentSeparatorOpacity: number;
  segmentSeparatorWidth: number;
};

const TOP_SEGMENT_OVERSCAN = 0.75;
const MIN_TOP_SEGMENT_HEIGHT = 3;

const renderSegmentSeparator = ({
  color,
  opacity,
  width,
  x,
  y,
  barWidth,
}: {
  color: string;
  opacity: number;
  width: number;
  x: number;
  y: number;
  barWidth: number;
}): ReactElement => (
  <line
    x1={x}
    y1={y}
    x2={x + barWidth}
    y2={y}
    stroke={color}
    strokeOpacity={opacity}
    strokeWidth={width}
    vectorEffect="non-scaling-stroke"
    pointerEvents="none"
  />
);

export const StackedBarChartRoundedTopBar = ({
  topSeriesKeyByRow,
  barRadius,
  segmentSeparatorColor,
  segmentSeparatorOpacity,
  segmentSeparatorWidth,
  ...props
}: StackedBarChartRoundedTopBarProps): JSX.Element => {
  const {
    ownerState,
    dataIndex,
    id,
    color,
    x,
    y,
    width,
    height: barHeight,
    layout,
    xOrigin,
    yOrigin,
    skipAnimation: skipAnimationProp,
    ...rest
  } = props;
  const animatedProps = useAnimateBar({
    x,
    y,
    width,
    height: barHeight,
    xOrigin,
    yOrigin,
    layout,
    skipAnimation: skipAnimationProp,
  });
  const animatedPathRef = animatedProps.ref as Ref<SVGPathElement>;
  const animatedX = animatedProps.x;
  const animatedY = animatedProps.y;
  const animatedWidth = animatedProps.width;
  const animatedHeight = animatedProps.height;
  const topKey = topSeriesKeyByRow[dataIndex];
  const isTop = typeof topKey === 'string' && topKey === String(id);
  const shouldRound = isTop && animatedHeight > 0;
  const filter = ownerState.isHighlighted ? 'brightness(120%)' : undefined;
  const opacity = ownerState.isFaded ? 0.3 : 1;
  const rawBottom = animatedY + animatedHeight;
  const topHeight =
    isTop && animatedHeight > 0
      ? Math.max(animatedHeight + TOP_SEGMENT_OVERSCAN, MIN_TOP_SEGMENT_HEIGHT)
      : animatedHeight;
  const topY = isTop && animatedHeight > 0 ? rawBottom - topHeight : animatedY;
  const radius = Math.min(barRadius, animatedWidth / 2, topHeight);
  const bottom = topY + topHeight;
  const separator =
    bottom < yOrigin - segmentSeparatorWidth / 2
      ? renderSegmentSeparator({
          color: segmentSeparatorColor,
          opacity: segmentSeparatorOpacity,
          width: segmentSeparatorWidth,
          x: animatedX,
          y: bottom,
          barWidth: animatedWidth,
        })
      : null;
  if (radius <= 0) {
    return (
      <g>
        <rect
          {...rest}
          x={animatedX}
          y={topY}
          width={animatedWidth}
          height={topHeight}
          fill={color}
          stroke="none"
          filter={filter}
          opacity={opacity}
          ref={animatedProps.ref}
          data-highlighted={ownerState.isHighlighted ? true : undefined}
          data-faded={ownerState.isFaded ? true : undefined}
        />
        {separator}
      </g>
    );
  }
  const right = animatedX + animatedWidth;
  if (!shouldRound) {
    return (
      <g>
        <rect
          {...rest}
          x={animatedX}
          y={topY}
          width={animatedWidth}
          height={topHeight}
          fill={color}
          stroke="none"
          filter={filter}
          opacity={opacity}
          ref={animatedProps.ref}
          data-highlighted={ownerState.isHighlighted ? true : undefined}
          data-faded={ownerState.isFaded ? true : undefined}
        />
        {separator}
      </g>
    );
  }
  const roundedPath = [
    `M ${animatedX} ${bottom}`,
    `L ${animatedX} ${topY + radius}`,
    `Q ${animatedX} ${topY} ${animatedX + radius} ${topY}`,
    `L ${right - radius} ${topY}`,
    `Q ${right} ${topY} ${right} ${topY + radius}`,
    `L ${right} ${bottom}`,
    'Z',
  ].join(' ');
  return (
    <g>
      <path
        {...rest}
        d={roundedPath}
        fill={color}
        stroke="none"
        filter={filter}
        opacity={opacity}
        ref={animatedPathRef}
        data-highlighted={ownerState.isHighlighted ? true : undefined}
        data-faded={ownerState.isFaded ? true : undefined}
      />
      {separator}
    </g>
  );
};
